import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tagsAPI } from '../../services/garageApi';
import BackButton from '../common/BackButton';

const TagEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [formData, setFormData] = useState({
        name: '',
        section: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    const fetchTag = useCallback(async () => {
        try {
            setFetching(true);
            const response = await tagsAPI.show(id);
            
            // Handle different response formats
            let tagData = null;
            if (response.data && response.data.data) {
                tagData = response.data.data;
            } else if (response.data) {
                tagData = response.data;
            }
            
            if (tagData) {
                setFormData({
                    name: tagData.name || '',
                    section: tagData.section || ''
                });
            } else {
                throw new Error('No tag data received');
            }
        } catch (error) {
            console.error('Error fetching tag:', error);
            alert('Error loading tag. Please try again.');
            navigate('/tags');
        } finally {
            setFetching(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        if (id) {
            fetchTag();
        }
    }, [id, fetchTag]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            await tagsAPI.update(id, formData);
            navigate('/tags');
        } catch (error) {
            console.error('Error updating tag:', error);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else {
                alert('Error updating tag. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }

    return (
        <div>
            <div className="bg-white shadow rounded-lg">
            <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <BackButton to="/tags" />
                    <h5 className="text-lg font-semibold">Edit Tag</h5>
                </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tag Name *
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.name ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Enter tag name"
                        />
                        {errors.name && (
                            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Section
                        </label>
                        <input
                            type="text"
                            name="section"
                            value={formData.section}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter section (optional)"
                        />
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end space-x-3">
                    <button
                        type="button"
                        onClick={() => navigate('/tags')}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                        {loading ? 'Updating...' : 'Update Tag'}
                    </button>
                </div>
            </form>
        </div>
        </div>
    );
};

export default TagEdit;